import {
    Button as AntdButton,
    Col as AntdCol,
    Row as AntdRow,
    Form,
    Input,
    Popconfirm,
    Table,
    Tooltip,
    notification,
    Image,
} from "antd";
import {ColumnsType} from "antd/es/table";
import React, {useEffect, useState} from "react";
import {Button, Card, Col, Row} from "react-bootstrap";
import {Icon} from "ts-react-feather-icons";
import CustomPagination from "../../components/CustomPagination";
import PageTitle from "../../components/PageTitle";
import {ActionType} from "../../constants/actionType";
import {UserRole} from "../../constants/userRole";
import {useUser} from "../../hooks";
import User from "../../model/User";
import {deleteEpisodeBackground, getEpisodeBackgroundDetail, getEpisodeBackgrounds} from "../../helpers/api/episode";
import {useParams} from "react-router-dom";
import EpisodeBackgroundDrawer from "./EpisodeBackgroundDrawer";

interface EpisodeBackgroundModel {
    key: React.Key;
    id: string;
    episodeId: string;
    name: string;
    backgroundUrl: string;
    createdAt: string;
    modifiedAt: string;
    createdBy: string;
    modifiedBy: string;
}

const EpisodeBackground = () => {

    const {storyId} = useParams();
    const {episodeId} = useParams();
    const [pageSize, setPageSize] = useState<number>(10);
    const [pageIndex, setPageIndex] = useState<number>(1);
    const [totalRecord, setTotalRecord] = useState<number>(0);
    const [backgrounds, setBackgrounds] = useState<EpisodeBackgroundModel[]>([]);
    const [backgroundDetail, setBackgroundDetail] = useState<EpisodeBackgroundModel | null>(null);
    const [actionType, setActionType] = useState<string>("");
    const [backgroundDrawerVisibility, setBackgroundDrawerVisibility] = useState<boolean>(false);

    const currentLoggedInUser = useUser().user as User || null;
    const [api, contextHolder] = notification.useNotification();

    const [form] = Form.useForm();
    const formValueObj = Form.useWatch([], form);
    const [searchLoading, setSearchLoading] = useState<boolean>(false);

    const showError = (errorMessage: string) => {
        api.error({
            message: `Error notification`,
            description: <span className="text-danger">{errorMessage || 'There\'re some errors occured!'}</span>,
            placement: 'topRight'
        });
    }

    const columns: ColumnsType<EpisodeBackgroundModel> = [
        {
            title: "No.",
            dataIndex: "noNumber"
        },
        {
            title: "Name",
            dataIndex: "name",
            render: (text, record, index) => {
                return <span>
                        {record.backgroundUrl && record.backgroundUrl.includes("mp4") ?
                            <video controls style={{width: '100px'}}>
                                <source src={record.backgroundUrl} type="video/mp4"/>
                                Your browser does not support the video element.
                            </video> :
                            <Image src={record.backgroundUrl} style={{maxWidth: "35px"}}/>}
                    {"  "}
                    {record.name}
                    </span>
            }
        },
        {
            title: "Created by",
            dataIndex: "createdBy",
        },
        {
            title: "Created at",
            dataIndex: "createdAt",
            render: (text, record, index) => {
                return <span>{record?.createdAt ? new Date(record?.createdAt).toLocaleString() : ''}</span>
            }
        },
        {
            title: "Modified by",
            dataIndex: "modifiedBy",
        },
        {
            title: "Action",
            dataIndex: "action",
            width: 80,
            render: (text, record, index) => {
                return (
                    (currentLoggedInUser?.roles?.includes(UserRole.SUPER_ADMIN) || currentLoggedInUser?.roles?.includes(UserRole.ADMIN)) && (
                        <>
                            <Tooltip title="Update">
                <span role="button"
                      onClick={() => triggerUpdate(record.id)}
                >
                  <Icon name="edit-2" color="#0d6efd" size={16}/>
                </span>
                            </Tooltip>
                            {" "}
                            {
                                (
                                    <Tooltip title="Delete">
                                        <Popconfirm
                                            title={`Delete background ${record.name}?`}
                                            description={`Are you sure to delete this background?`}
                                            onConfirm={() => triggerDelete(record.id)}
                                            okText="OK"
                                            cancelText="Cancel"
                                        >
                      <span role="button" className="d-inline-block ms-1">
                        <Icon name="trash-2" color="#dc3545" size={16}/>
                      </span>
                                        </Popconfirm>
                                    </Tooltip>
                                )
                            }
                        </>
                    )
                );
            },
        },
    ];

    useEffect(() => {
        if (!episodeId) {
            return;
        }
        search();
    }, [pageIndex]);

    useEffect(() => {
        if (!episodeId) {
            return;
        }
        if (pageIndex === 1) {
            search();
            return;
        }
        setPageIndex(1);
    }, [pageSize]);

    const search = async () => {
        setSearchLoading(true);
        const searchObj: any = {
            pageIndex: pageIndex - 1,
            pageSize,
        };
        if (formValueObj?.name) {
            searchObj["name"] = formValueObj?.name.trim();
        }
        if (formValueObj?.status) {
            searchObj["status"] = formValueObj?.status;
        }
        try {
            const response = await getEpisodeBackgrounds(episodeId as string, searchObj);
            if (response.status === 200) {
                const data: any[] = [];
                response.data.data.forEach((item: any, index: number) => {
                    data.push({
                        ...item,
                        noNumber: (pageIndex - 1) * pageSize + index + 1,
                    });
                });
                setBackgrounds(data);
                setTotalRecord(response.data.totalRecord);
            }
        } catch (err) {
            console.error(err);
            api.error({
                message: `Error notification`,
                description: <span className="text-danger">{err as string || 'There\'re some errors occured!'}</span>,
                placement: 'topRight'
            });
        }
        setSearchLoading(false);
    }

    const triggerDelete = async (backgroundId: string) => {
        try {
            const response = await deleteEpisodeBackground(episodeId as string, backgroundId);
            if (response.status === 200) {
                api.success({
                    message: `Successfull notification`,
                    description: 'Delete background successfully!',
                    placement: 'topRight'
                });
                await search();
            }
        } catch (err) {
            showError(err as string);
        }
    }

    const triggerUpdate = async (backgroundId: string) => {
        try {
            if (!backgroundId || backgroundId?.trim()?.length === 0) {
                return;
            }
            const background = await getEpisodeBackgroundDetail(episodeId as string, backgroundId);
            setBackgroundDetail(background.data);
            setActionType(ActionType.UPDATE);
            setBackgroundDrawerVisibility(true);
        } catch (err) {
            console.error(err);
            showError(err as string);
        }
    }

    return (
        <>
            {contextHolder}

            <EpisodeBackgroundDrawer
                episodeId={episodeId}
                drawerVisibility={backgroundDrawerVisibility}
                setdDrawerVisibility={setBackgroundDrawerVisibility}
                backgroundDetail={backgroundDetail}
                setBackgroundDetail={setBackgroundDetail}
                actionType={actionType}
                setActionType={setActionType}
                onSuccess={() => {
                    api.success({
                        message: `Successfull notification`,
                        description: (actionType === ActionType.CREATE ? "Create" : "Update") + ' category successfully!',
                        placement: 'topRight'
                    });
                    setBackgroundDetail(null);
                    setActionType("");
                    search();
                }}
                onFailure={(errorMessage: string) => {
                    showError(errorMessage);
                }}
            />

            <PageTitle
                breadCrumbItems={[
                    {label: "Stories", path: "/stories"},
                    {label: "Episodes", path: `/stories/${storyId}/episodes`},
                    {label: "Backgrounds", path: `/stories/${storyId}/episodes/${episodeId}/background`},
                ]}
                title={"Backgrounds"}
            />

            <Row>
                <Col>
                    <Card>
                        <Card.Body>
                            <Form layout="vertical" autoComplete='off' form={form}>
                                <AntdRow gutter={16}>
                                    <AntdCol span={12}>
                                        <Form.Item
                                            name="name"
                                            label="Name"
                                        >
                                            <Input placeholder="Please enter name"/>
                                        </Form.Item>
                                    </AntdCol>
                                </AntdRow>
                                <AntdRow className="justify-content-center">
                                    <AntdCol>
                                        <AntdButton type="primary" className="me-1" htmlType="submit"
                                                    loading={searchLoading}
                                                    onClick={() => {
                                                        if (pageIndex === 1) {
                                                            search();
                                                            return;
                                                        }
                                                        setPageIndex(1);
                                                    }}
                                        >
                                            Search
                                        </AntdButton>
                                        <AntdButton type="default" loading={searchLoading} onClick={() => {
                                            form.resetFields();
                                            if (pageIndex === 1) {
                                                search();
                                                return;
                                            }
                                            setPageIndex(1);
                                        }}>
                                            Reset
                                        </AntdButton>
                                    </AntdCol>
                                </AntdRow>
                            </Form>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Row>
                <Col>
                    <Card>
                        <Card.Body>
                            <Row>
                                <Col sm={12} className="mb-2">
                                    <div className="text-sm-end">
                                        <Button variant="primary" className="mb-1 py-1 me-1" onClick={() => {
                                            setActionType(ActionType.CREATE);
                                            setBackgroundDrawerVisibility(true);
                                        }}>
                                            <i className="mdi mdi-plus-circle me-1"></i> Create
                                        </Button>
                                    </div>
                                </Col>
                            </Row>

                            <Table
                                loading={searchLoading}
                                rowKey={"noNumber"}
                                className="table table-hover m-0 table-centered dt-responsive nowrap w-100"
                                dataSource={backgrounds}
                                columns={columns}
                                pagination={false}
                            />

                            {backgrounds?.length !== 0 && (
                                <CustomPagination
                                    pageIndex={pageIndex}
                                    pageSize={pageSize}
                                    totalItem={totalRecord}
                                    setPageSize={setPageSize}
                                    setPageIndex={setPageIndex}
                                />
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </>
    );
};

export default EpisodeBackground;
